import AuthStore from "@/AuthStore";
import { RouteSerializer } from "@/router/RouteSerializer";
import store from "@/store/store";
import { CorePermission, Logger } from "@iloq/shared";
import { NavigationGuard } from "vue-router";

export const routerGuard: NavigationGuard = (to, from, next): any => {
  Logger.debug("routing from:", from.fullPath, "to:", to.fullPath);

  if (to?.matched?.every((record) => !record.meta.authRequired)) {
    Logger.debug("no auth required for", to.path);
    return next();
  }

  if (!AuthStore.hasRefreshToken()) {
    Logger.debug("No refresh token, need to login");
    RouteSerializer.saveRoute(to);
    return next({ name: "re-login" });
  }

  const checkPermissions = () => {
    const userSubsidiaryId = store.state.user?.subsidiaryId;
    const allDisabledFors = to?.matched?.map((record) => record?.meta?.disabledFor || []).flat();

    const { redirectRouteName } =
      allDisabledFors?.find(({ id }: { id: string }) => id === userSubsidiaryId) || {};

    if (redirectRouteName) {
      return next({ name: redirectRouteName });
    }

    const userCountryCode = store.state.user?.countryCode;
    const onlyAllowedForSpecificCountries = to?.matched
      ?.map((record) => record?.meta?.onlyAllowedForSpecificCountries)
      .filter((obj) => !!obj);

    if (onlyAllowedForSpecificCountries.length) {
      const { redirectRouteName } =
        onlyAllowedForSpecificCountries.find(
          ({ countries }) => !countries?.includes(userCountryCode),
        ) || {};
      if (redirectRouteName) {
        return next({ name: redirectRouteName });
      }
    }

    if (to?.matched?.some((record) => record?.meta?.permissionsRequired)) {
      const permissionSet = new Set<CorePermission>();
      to?.matched?.forEach((record) =>
        record?.meta?.permissionsRequired?.forEach((permission: CorePermission) =>
          permissionSet.add(permission),
        ),
      );
      const permissions = Array.from(permissionSet);
      if (!store.state.user || !store.state.user.hasPermissions(permissions)) {
        Logger.warn("User not authorized for", to.path);
        return next({ name: "forbidden" });
      }
      Logger.debug("Permissions OK");
      return next();
    }
    Logger.debug("No permissions needed");
    return next();
  };

  const continueRoute = () => {
    // Continue to the original target route before the login
    if (from?.path?.startsWith("/tokens/")) {
      const storedRoute = RouteSerializer.loadSavedRoute();
      if (storedRoute) {
        Logger.debug("routing to previous target path:", storedRoute);
        sessionStorage.removeItem("nextRoute");
        return next(storedRoute);
      }
    }
    return checkPermissions();
  };

  const ensureUser = () => {
    if (!store.state.user) {
      Logger.debug("No user, need to fetch");
      return store
        .dispatch("fetchUser")
        .then(() => continueRoute())
        .catch(() => {
          Logger.warn("User fetch failed, redirecting to re-login");
          RouteSerializer.saveRoute(to);
          return next("/re-login");
        });
    }
    return continueRoute();
  };

  if (!AuthStore.loadAuthToken()) {
    Logger.debug("No stored auth token - fetch a new one");
    return store.dispatch("refreshAuthToken").then(
      () => {
        Logger.debug("auth token fetched, load user");
        return ensureUser();
      },
      () => {
        Logger.warn("failed to fetch new auth token, redirecting to re-login");
        RouteSerializer.saveRoute(to);
        return next("/re-login");
      },
    );
  }

  return ensureUser();
};
